/**
 * Per-channel E2EE draft store — IndexedDB-backed.
 *
 * This module is the web counterpart of
 * `kaether_for_ios/Stoat/E2EE/DraftStore.swift`. It is intentionally
 * separate from the local-only UX store at
 * `packages/client/components/state/stores/Draft.ts`:
 *
 *   - `state/stores/Draft.ts` — ephemeral, in-memory SolidJS signal store
 *     for the *current tab's* composer text, file attachments, and reply
 *     intents. It is never encrypted and never written to disk.
 *
 *   - `e2ee/drafts/DraftStore.ts` (this file) — persistent, cross-device
 *     E2EE store for draft text received from or dispatched to the user's
 *     other devices. It writes to IndexedDB (analogous to iOS's
 *     `.completeFileProtection` file store) and participates in the
 *     `kaether:draft:v1:` encrypted-sync protocol.
 *
 * The two stores are deliberately decoupled. The UI layer is responsible for
 * bridging them on view appear / disappear as described in
 * `kaether_for_web/docs/drafts-per-thread.md`.
 *
 * ## Monotonicity rule
 *
 * One draft record per channel; strictly-later ts wins.
 *
 *   - Strictly-older updates are dropped silently — a hostile server
 *     replaying a stale "clear" event cannot wipe a fresher draft.
 *   - Equal-ts updates are no-ops (idempotent).
 *   - Strictly-newer updates overwrite, even if the new payload is empty
 *     (which is the canonical "I cleared my draft" signal).
 *
 * ## Tie-break for equal timestamps
 *
 * When two devices author a draft at the same Unix second (`ts_A == ts_B`),
 * neither can be proven more recent. The store applies a deterministic
 * no-op: the first writer wins and the second update is discarded. This
 * matches iOS behaviour and prevents `observedAt` churn for duplicate
 * delivery.
 *
 * Mirrors `kaether_for_ios/Stoat/E2EE/DraftStore.swift`.
 */

import type { DraftPayload } from "./DraftMessage";

// ---------------------------------------------------------------------------
// Record stored in IndexedDB
// ---------------------------------------------------------------------------

export interface DraftRecord {
  readonly channelId: string;
  readonly text: string;
  readonly ts: number;
  readonly observedAt: number; // Unix ms, local time when we recorded it
}

// ---------------------------------------------------------------------------
// IndexedDB plumbing
// ---------------------------------------------------------------------------

const DB_NAME = "kaether:drafts:v1";
const STORE_NAME = "drafts";
const DB_VERSION = 1;

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        // keyed by channelId — one record per channel
        db.createObjectStore(STORE_NAME, { keyPath: "channelId" });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

async function idbGet(
  db: IDBDatabase,
  channelId: string,
): Promise<DraftRecord | undefined> {
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, "readonly");
    const req = tx.objectStore(STORE_NAME).get(channelId);
    req.onsuccess = () => resolve(req.result as DraftRecord | undefined);
    req.onerror = () => reject(req.error);
  });
}

async function idbPut(db: IDBDatabase, record: DraftRecord): Promise<void> {
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, "readwrite");
    const req = tx.objectStore(STORE_NAME).put(record);
    req.onsuccess = () => resolve();
    req.onerror = () => reject(req.error);
  });
}

async function idbDelete(db: IDBDatabase, channelId: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, "readwrite");
    const req = tx.objectStore(STORE_NAME).delete(channelId);
    req.onsuccess = () => resolve();
    req.onerror = () => reject(req.error);
  });
}

async function idbGetAll(db: IDBDatabase): Promise<DraftRecord[]> {
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, "readonly");
    const req = tx.objectStore(STORE_NAME).getAll();
    req.onsuccess = () => resolve(req.result as DraftRecord[]);
    req.onerror = () => reject(req.error);
  });
}

async function idbClear(db: IDBDatabase): Promise<void> {
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, "readwrite");
    const req = tx.objectStore(STORE_NAME).clear();
    req.onsuccess = () => resolve();
    req.onerror = () => reject(req.error);
  });
}

// ---------------------------------------------------------------------------
// DraftStore
// ---------------------------------------------------------------------------

/**
 * E2EE-aware, IndexedDB-backed draft store.
 *
 * All methods are async because every read/write goes through IndexedDB.
 * The store is safe to instantiate multiple times with the same `dbName` —
 * the underlying IDB object store is shared (mirrors iOS multi-instance
 * persistence test).
 *
 * For test isolation, pass a unique `dbName` per test case.
 */
export class DraftStore {
  private readonly dbName: string;
  private dbPromise: Promise<IDBDatabase> | null = null;

  constructor(dbName: string = DB_NAME) {
    this.dbName = dbName;
  }

  private db(): Promise<IDBDatabase> {
    if (!this.dbPromise) {
      this.dbPromise = new Promise((resolve, reject) => {
        const req = indexedDB.open(this.dbName, DB_VERSION);
        req.onupgradeneeded = () => {
          const db = req.result;
          if (!db.objectStoreNames.contains(STORE_NAME)) {
            db.createObjectStore(STORE_NAME, { keyPath: "channelId" });
          }
        };
        req.onsuccess = () => resolve(req.result);
        req.onerror = () => reject(req.error);
      });
    }
    return this.dbPromise;
  }

  // -------------------------------------------------------------------------
  // Recording
  // -------------------------------------------------------------------------

  /**
   * Apply an incoming (or local) draft payload.
   *
   * Returns `true` if the stored state changed.
   *
   * Monotonicity:
   *   - Strictly-older ts → dropped (returns false).
   *   - Equal ts → no-op (returns false). Tie-break: first writer wins.
   *   - Strictly-newer ts with empty text → deletes the record (clear signal).
   *   - Strictly-newer ts with non-empty text → writes the record.
   */
  async apply(
    payload: DraftPayload,
    observedAt: number = Date.now(),
  ): Promise<boolean> {
    const db = await this.db();
    const existing = await idbGet(db, payload.channelId);

    if (existing !== undefined && payload.ts <= existing.ts) {
      // Equal-ts or older — no-op. First writer wins on tie.
      return false;
    }

    if (payload.text === "") {
      // Clear-draft signal: delete the record so it does not appear in
      // `allDrafts()` as a phantom entry.
      if (existing !== undefined) {
        await idbDelete(db, payload.channelId);
        return true;
      }
      // Nothing to delete.
      return false;
    }

    const record: DraftRecord = {
      channelId: payload.channelId,
      text: payload.text,
      ts: payload.ts,
      observedAt,
    };
    await idbPut(db, record);
    return true;
  }

  /**
   * Local-author convenience: persist a draft we just typed and return
   * `true` if state changed.
   *
   * The caller is responsible for encrypting a `DraftPayload` and shipping
   * it to the user's own other devices via the standard E2EE channel —
   * `DraftStore` is purely local persistence.
   */
  async setLocal(
    channelId: string,
    text: string,
    ts: number = Math.floor(Date.now() / 1000),
    observedAt: number = Date.now(),
  ): Promise<boolean> {
    return this.apply(
      { schema: 1, channelId, text, ts },
      observedAt,
    );
  }

  /**
   * Equivalent to `setLocal` with empty text and a now-ts.
   * Mirrors iOS `DraftStore.clear(channelId:)`.
   */
  async clear(
    channelId: string,
    ts: number = Math.floor(Date.now() / 1000),
    observedAt: number = Date.now(),
  ): Promise<boolean> {
    return this.setLocal(channelId, "", ts, observedAt);
  }

  // -------------------------------------------------------------------------
  // Queries
  // -------------------------------------------------------------------------

  /** Returns the stored draft for `channelId`, or `undefined` if none. */
  async draft(channelId: string): Promise<DraftRecord | undefined> {
    const db = await this.db();
    return idbGet(db, channelId);
  }

  /**
   * All non-empty drafts, sorted by `observedAt` descending — for a
   * cross-thread "you have drafts" UI surface.
   */
  async allDrafts(): Promise<DraftRecord[]> {
    const db = await this.db();
    const all = await idbGetAll(db);
    return all.sort((a, b) => b.observedAt - a.observedAt);
  }

  // -------------------------------------------------------------------------
  // Reset
  // -------------------------------------------------------------------------

  /** Drop every stored draft. Used in tests and on sign-out. */
  async resetAll(): Promise<void> {
    const db = await this.db();
    await idbClear(db);
  }
}

// ---------------------------------------------------------------------------
// Shared singleton (production use)
// ---------------------------------------------------------------------------

/**
 * Shared singleton for production code. Tests should instantiate their own
 * `DraftStore` with a unique `dbName` for isolation.
 */
export const sharedDraftStore = new DraftStore();
